import React, { useEffect, useState } from 'react';
import type { TeamSecretDetail } from '../client';
import Modal from '../components/Modal';
import { memberDisplayName } from '../components/MemberDetailModal';
import { useApi } from '../state/ApiContext';
import SecretDetail, { SecretAgent } from './SecretDetail';
import type { SecretActivityMember } from './SecretActivity';

interface SecretDetailsModalProps {
  teamId: number;
  secretId: string | null;
  onClose: () => void;
  onChanged?: (detail: TeamSecretDetail) => void;
  onDeleted?: (secretId: string) => void;
}

const SecretDetailsModal: React.FC<SecretDetailsModalProps> = ({
  teamId,
  secretId,
  onClose,
  onChanged,
  onDeleted,
}) => {
  const { teamApi } = useApi();
  const [agents, setAgents] = useState<SecretAgent[]>([]);
  const [members, setMembers] = useState<SecretActivityMember[]>([]);

  useEffect(() => {
    if (!secretId) return;
    let cancelled = false;
    void Promise.allSettled([
      teamApi.apiTeamTeamIdAgentsGet(teamId),
      teamApi.apiTeamTeamIdUsersGet(teamId),
    ]).then(([agentsResult, membersResult]) => {
      if (cancelled) return;
      if (agentsResult.status === 'fulfilled') {
        setAgents(
          agentsResult.value.data
            .filter((agent) => agent.id !== undefined)
            .map((agent) => ({ id: agent.id!, name: agent.name })),
        );
      }
      if (membersResult.status === 'fulfilled') {
        setMembers(
          membersResult.value.data
            .filter((member) => member.id !== undefined)
            .map((member) => ({
              id: member.id!,
              name: memberDisplayName(member),
            })),
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [secretId, teamApi, teamId]);

  return (
    <Modal
      isOpen={secretId !== null}
      onClose={onClose}
      title="Secret details"
      maxWidth="960px"
      fullScreenOnMobile>
      {secretId && (
        <SecretDetail
          teamId={teamId}
          secretId={secretId}
          agents={agents}
          members={members}
          onChanged={onChanged}
          onDeleted={() => {
            onDeleted?.(secretId);
            onClose();
          }}
        />
      )}
    </Modal>
  );
};

export default SecretDetailsModal;
